import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminBanService } from '../../services/banService';
import type { BanType } from '../../types/ban';
import AdminBanTable from './components/AdminBanTable';
import AdminBanFormModal from './components/AdminBanFormModal';
import WatchlistTab from './components/WatchlistTab';

type ActiveTab = BanType | 'watchlist';

export default function BanManagementPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('ip');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBan, setEditingBan] = useState<any>(null);
  const queryClient = useQueryClient();

  // Reset selections when changing tabs
  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    setSearch('');
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // Fetch data based on active tab
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'bans', activeTab, { search, page: currentPage }],
    enabled: activeTab !== 'watchlist',
    queryFn: () => {
      const params = { search, page: currentPage };
      switch (activeTab) {
        case 'ip':
          return adminBanService.getGlobalIpBans(params);
        case 'domain':
          return adminBanService.getGlobalDomainBans(params);
        case 'isp':
          return adminBanService.getGlobalIspBans(params);
        case 'asn':
          return adminBanService.getGlobalAsnBans(params);
        default:
          return Promise.resolve(undefined);
      }
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => {
      switch (activeTab) {
        case 'ip':
          return adminBanService.createGlobalIpBan(data);
        case 'domain':
          return adminBanService.createGlobalDomainBan(data);
        case 'isp':
          return adminBanService.createGlobalIspBan(data);
        case 'asn':
          return adminBanService.createGlobalAsnBan(data);
        default:
          return Promise.resolve(undefined);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bans', activeTab] });
      toast.success('Ban created successfully');
      setShowModal(false);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        Object.values(error.response?.data?.errors || {})[0]?.[0] ||
        'Failed to create ban';
      toast.error(message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => {
      switch (activeTab) {
        case 'ip':
          return adminBanService.updateGlobalIpBan(id, data);
        case 'domain':
          return adminBanService.updateGlobalDomainBan(id, data);
        case 'isp':
          return adminBanService.updateGlobalIspBan(id, data);
        case 'asn':
          return adminBanService.updateGlobalAsnBan(id, data);
        default:
          return Promise.resolve(undefined);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bans', activeTab] });
      toast.success('Ban updated successfully');
      setEditingBan(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update ban');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => {
      switch (activeTab) {
        case 'ip':
          return adminBanService.deleteGlobalIpBan(id);
        case 'domain':
          return adminBanService.deleteGlobalDomainBan(id);
        case 'isp':
          return adminBanService.deleteGlobalIspBan(id);
        case 'asn':
          return adminBanService.deleteGlobalAsnBan(id);
        default:
          return Promise.resolve(undefined);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bans', activeTab] });
      toast.success('Ban removed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove ban');
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: () => adminBanService.bulkDelete(activeTab, selectedIds),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bans', activeTab] });
      toast.success(response.message);
      setSelectedIds([]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete bans');
    },
  });

  // Bulk toggle status mutation
  const bulkToggleStatusMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      adminBanService.bulkToggleStatus(activeTab, selectedIds, isActive),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bans', activeTab] });
      toast.success(response.message);
      setSelectedIds([]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update bans');
    },
  });

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to remove this ban?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = () => {
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedIds.length} ban(s)?`
      )
    ) {
      bulkDeleteMutation.mutate();
    }
  };

  const handleBulkActivate = () => {
    bulkToggleStatusMutation.mutate(true);
  };

  const handleBulkDeactivate = () => {
    bulkToggleStatusMutation.mutate(false);
  };

  const tabs: { key: ActiveTab; label: string }[] = [
    { key: 'ip', label: 'IPs' },
    { key: 'domain', label: 'Domains' },
    { key: 'isp', label: 'ISPs' },
    { key: 'asn', label: 'ASNs' },
    { key: 'watchlist', label: '⚠ Watchlist' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Ban Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage global traffic bans across all accounts
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium">
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          GLOBAL - Affects All Accounts
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'watchlist' ? (
        <WatchlistTab />
      ) : (
        <>
          {/* Search & Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="input w-full sm:max-w-md"
            />
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              + Add {activeTab.toUpperCase()} Ban
            </button>
          </div>

          {/* Bulk Actions Bar */}
          {selectedIds.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedIds.length} selected
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleBulkActivate}
                  className="btn btn-secondary btn-sm"
                  disabled={bulkToggleStatusMutation.isPending}
                >
                  Activate
                </button>
                <button
                  onClick={handleBulkDeactivate}
                  className="btn btn-secondary btn-sm"
                  disabled={bulkToggleStatusMutation.isPending}
                >
                  Deactivate
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="btn btn-danger btn-sm"
                  disabled={bulkDeleteMutation.isPending}
                >
                  Delete
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <AdminBanTable
            type={activeTab as BanType}
            data={data?.data || []}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onEdit={setEditingBan}
            onDelete={handleDelete}
            onToggleStatus={(ban) =>
              updateMutation.mutate({
                id: ban.id,
                data: { is_active: !ban.is_active },
              })
            }
          />

          {/* Pagination */}
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between px-4 py-3">
              <div className="text-sm text-gray-600">
                Showing {data.from} to {data.to} of {data.total} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(data.last_page, p + 1))
                  }
                  disabled={currentPage === data.last_page}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Modal for Add/Edit */}
          {(showModal || editingBan) && (
            <AdminBanFormModal
              type={activeTab as BanType}
              ban={editingBan}
              onClose={() => {
                setShowModal(false);
                setEditingBan(null);
              }}
              onSubmit={(data) => {
                if (editingBan) {
                  updateMutation.mutate({ id: editingBan.id, data });
                } else {
                  createMutation.mutate(data);
                }
              }}
              isSubmitting={createMutation.isPending || updateMutation.isPending}
            />
          )}
        </>
      )}
    </div>
  );
}
